// Aba "Dispositivos" do pedido: o device capturado em CADA tentativa de
// pagamento (GET /orders/{orderId}/devices).
//
// A listagem é histórico, não "o device do pedido": retentativa insere outro
// registro e, na recorrência, cada ciclo cobrado também. Por isso cada cartão
// mostra quando foi capturado e — quando recorrência — o ciclo, senão duas
// linhas do mesmo pedido ficam indistinguíveis.
import {
  MonitorSmartphoneIcon,
  RepeatIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useOrderDevices } from "@/hooks"

const prefix = "orders.details.devices"

type DevicesTabProps = {
  orderId: string
}

/** Junta as duas dimensões da tela num rótulo só ("1512 × 982"). */
const screenOf = (
  width?: number | null,
  height?: number | null
) => (width && height ? `${width} × ${height}` : null)

/** Coordenadas só fazem sentido em par. */
const geolocationOf = (
  latitude?: number | null,
  longitude?: number | null
) =>
  latitude != null && longitude != null
    ? `${latitude}, ${longitude}`
    : null

const browserOf = (
  name?: string | null,
  version?: string | null
) => [name, version].filter(Boolean).join(" ") || null

export const DevicesTab = ({
  orderId,
}: DevicesTabProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useOrderDevices(orderId)

  if (isLoading) {
    return (
      <div
        data-testid="order-devices-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton
            key={`order-devices-skeleton-${index}`}
            className="h-40 w-full rounded-lg"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${prefix}.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${prefix}.retry`)}
        </Button>
      </div>
    )
  }

  const devices = data ?? []

  if (devices.length === 0) {
    return (
      <div
        data-testid="order-devices-empty"
        className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center"
      >
        <MonitorSmartphoneIcon
          className="size-6 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-sm font-medium">
          {t(`${prefix}.empty.title`)}
        </p>
        <p className="text-xs text-muted-foreground">
          {t(`${prefix}.empty.description`)}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {t(`${prefix}.hint`, { count: devices.length })}
      </p>

      {devices.map((device) => (
        <SectionCard
          key={device.id}
          title={
            device.createdAt ??
            t(`${prefix}.captureUnknown`)
          }
          icon={<MonitorSmartphoneIcon size={16} />}
          action={
            device.subscription ? (
              <Badge
                variant="outline"
                data-testid={`order-device-cycle-${device.id}`}
                className="gap-1 rounded-full border border-blue-200 bg-blue-50 font-medium text-blue-700"
              >
                <RepeatIcon
                  className="size-3"
                  aria-hidden="true"
                />
                {t(`${prefix}.cycleBadge`, {
                  cycle: device.billingCycle ?? "—",
                })}
              </Badge>
            ) : undefined
          }
        >
          <div
            data-testid={`order-device-${device.id}`}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            <InfoItem
              label={t(`${prefix}.fields.ipAddress`)}
              value={device.ipAddress}
              copyable
            />
            <InfoItem
              label={t(`${prefix}.fields.deviceId`)}
              value={device.deviceId}
              copyable
            />
            <InfoItem
              label={t(`${prefix}.fields.fingerprint`)}
              value={device.fingerprint}
              copyable
            />
            <InfoItem
              label={t(`${prefix}.fields.platform`)}
              value={device.platform}
            />
            <InfoItem
              label={t(`${prefix}.fields.browser`)}
              value={browserOf(
                device.browserName,
                device.browserVersion
              )}
            />
            <InfoItem
              label={t(`${prefix}.fields.language`)}
              value={device.language}
            />
            <InfoItem
              label={t(`${prefix}.fields.screen`)}
              value={screenOf(
                device.screenWidth,
                device.screenHeight
              )}
            />
            <InfoItem
              label={t(`${prefix}.fields.geolocation`)}
              value={geolocationOf(
                device.latitude,
                device.longitude
              )}
              copyable
            />
            {device.subscription && (
              <InfoItem
                label={t(`${prefix}.fields.subscriptionId`)}
                value={device.subscriptionId}
                copyable
              />
            )}
            <InfoItem
              label={t(`${prefix}.fields.userAgent`)}
              value={device.userAgent}
              className="sm:col-span-2 lg:col-span-3"
            />
          </div>
        </SectionCard>
      ))}
    </div>
  )
}
